import { message } from 'antd';
import { isEqual, omitBy } from 'lodash-es';
import moment from 'moment';

interface DataItem {
  createdAt: string;
  id: string;
  [key: string]: any;
}

interface Location {
  country?: string;
  city_count?: number;
  [key: string]: any;
}

interface SortableObject {
  city_count: number;
  [key: string]: any;
}

export const groupByDate = (data: DataItem[]): { date: string; messages: DataItem[] }[] => {
  const groupedData: { [key: string]: DataItem[] } = {};

  for (const item of data) {
    const date = item.createdAt.slice(0, 10);
    if (!groupedData[date]) {
      groupedData[date] = [];
    }
    if (!groupedData[date].some((i) => i.id === item.id)) {
      groupedData[date].unshift(item);
    }
  }

  const result: { date: string; messages: DataItem[] }[] = [];
  Object.keys(groupedData)
    .sort()
    .forEach((date) => {
      result.push({ date: date, messages: groupedData[date] });
    });
  return result;
};

export const getUserInitials = (name: any) => {
  return name?.match(/(\b\S)?/g).join('');
};

export const docStatusColor = (docStatus: string): { statusColor: string; statusText: string } => {
  let statusColor: string;
  let statusText: string;

  switch (docStatus.toLowerCase()) {
    case 'completed':
    case 'signed':
      statusColor = 'green';
      statusText = 'Signed';
      break;
    case 'sent':
    case 'signing':
      statusColor = '#4094F7';
      statusText = 'Signing';
      break;
    case 'draft':
      statusColor = 'gray';
      statusText = 'Draft';
      break;
    default:
      statusColor = 'black';
      statusText = 'Unsigned';
      break;
  }

  return { statusColor, statusText };
};

export const capitalizeFirstLetter = (str: string): string => {
  const arrayOfSplitString = str.split(' ');
  const result = arrayOfSplitString.map((word) => word.charAt(0).toUpperCase() + word.substring(1));
  return result.join(' ');
};

export const copyTextToClipboard = async (text: string): Promise<void> => {
  if ('clipboard' in navigator) {
    return navigator.clipboard.writeText(text);
  } else {
    document.execCommand('copy', true, text);
  }
};

export const beforeUpload = (file) => {
  const isJpgOrPng =
    file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/svg+xml';
  if (!isJpgOrPng) {
    message.error('You can only upload JPG/PNG/SVG file!');
  }
  let isLt2M = false;
  if (file.type === 'image/svg+xml') {
    // Adjust size limit for SVG files
    isLt2M = file.size / 1024 < 500; // 500KB for SVG files
  } else {
    // For JPEG/PNG files
    isLt2M = file.size / 1024 / 1024 < 1; // 2MB for JPEG/PNG files
  }
  if (!isLt2M) {
    message.error('Image must be smaller than 2MB (or 500KB for SVG)!');
  }
  return isJpgOrPng && isLt2M;
};

export const downloadCSV = (data: string, filename: string): void => {
  const link = document.createElement('a');
  if (!data) return;

  filename = `${filename}.csv`;

  if (!data.match(/^data:text\/csv/i)) {
    data = `data:text/csv;charset=utf-8,${data}`;
  }

  link.setAttribute('href', encodeURI(data));
  link.setAttribute('download', filename);
  link.click();
};

export const convertIPv6AddresstoIPv4 = (ip6Address: string): string => {
  const parsedString = parseIPv6ToString(ip6Address);

  const ip4Address = `${parsedString[6] >> 8}.${parsedString[6] & 0xff}.${parsedString[7] >> 8}.${
    parsedString[7] & 0xff
  }`;

  if (ip4Address === '0.0.0.0') {
    return ip6Address;
  }

  return ip4Address;
};

export const groupLocationByCountry = (
  dataArray: Location[] = []
): { country: string | undefined; cities: Location[] }[] => {
  const groupedData = dataArray.reduce((map, location) => {
    const country = location?.country;

    if (!map.has(country)) {
      map.set(country, [location]);
    } else {
      const newLocation = map.get(country)!;
      newLocation.unshift(location);
      map.set(country, newLocation);
    }

    return map;
  }, new Map<string | undefined, Location[]>());

  const groupedCitiesArrays: { country: string | undefined; cities: Location[] }[] = [];

  for (const [key, cities] of groupedData.entries()) {
    groupedCitiesArrays.unshift({
      country: key,
      cities
    });
  }

  return groupedCitiesArrays.sort((countryA, countryB) => {
    const countryAPeopleCount = countryA.cities.reduce(
      (prev, curr) => prev + Number(curr.city_count),
      0
    );
    const countryBPeopleCount = countryB.cities.reduce(
      (prev, curr) => prev + Number(curr.city_count),
      0
    );

    return countryBPeopleCount - countryAPeopleCount;
  });
};

export const sortArrayOfObjectsNumerically = (array: SortableObject[]): SortableObject[] =>
  array.sort((a, b) => b.city_count - a.city_count);

export const getArrayOfMonths = (startDate: string): string[] => {
  const createdDate = new Date(startDate);
  createdDate.setDate(1); // Set to the first day of the month
  const currentDate = new Date();
  const dateAndYearList = [createdDate.toLocaleString('en', { month: 'short' })];

  while (createdDate.getTime() < currentDate.getTime()) {
    createdDate.setMonth(createdDate.getMonth() + 1);
    dateAndYearList.push(createdDate.toLocaleString('en', { month: 'short' }));
  }

  return dateAndYearList;
};

export const generateArrayOfYears = (dateToCompare: string): number[] => {
  const splitDateString = dateToCompare.split('T');
  const date1 = new Date(splitDateString[0]);
  const max = new Date().getFullYear();

  const timeDiff = Math.abs(date1.getFullYear() - max);
  let years: number[] = [];
  if (timeDiff <= 0) {
    years.push(max);
  } else {
    const min = max - timeDiff;

    for (let i = max; i >= min; i--) {
      years.push(i);
    }
  }

  if (years.length > 1) {
    years = years.sort((a, b) => a - b);
  }

  return years;
};

export function deepCheck(oldObj: any, newObj: any): any {
  let diff: any = {};

  if (Array.isArray(oldObj)) {
    const tempArr: any[] = [];
    for (let i = 0; i < oldObj.length; i++) {
      const element = deepCheck(oldObj[i], newObj[i]);
      if (Object.keys(element).length) {
        tempArr.push(element);
      }
    }

    if (tempArr.length) {
      diff = tempArr;
    }
  } else if (typeof oldObj === 'object' && oldObj !== null) {
    for (const key in oldObj) {
      if (Array.isArray(oldObj[key])) {
        const tempArr: any[] = [];
        for (let i = 0; i < oldObj[key].length; i++) {
          const element = deepCheck(oldObj[key][i], newObj[key][i]);
          if (Object.keys(element).length) {
            tempArr.push(element);
          }
        }

        if (tempArr.length) {
          diff[key] = tempArr;
        }
      } else if (typeof oldObj[key] === 'object' && oldObj[key] !== null) {
        const element = deepCheck(oldObj[key], newObj[key]);
        if (Object.keys(element).length) {
          diff[key] = element;
        }
      } else {
        if (oldObj[key] !== newObj[key]) {
          diff[key] = newObj[key];
        }
      }
    }
  } else {
    if (oldObj !== newObj) {
      diff = newObj;
    }
  }

  return diff;
}

export const decodeJwt = (token: string | undefined): any | null => {
  try {
    const base64EncodedUrl = token?.split('.')[1];
    if (!base64EncodedUrl) return null;
    const base64TokenString = base64EncodedUrl.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      window
        .atob(base64TokenString)
        .split('')
        .map((char) => '%' + ('00' + char.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );

    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return null;
  }
};

export const getBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

export const getChanges = (original, updated) =>
  omitBy(updated, (value, key) => isEqual(original[key], value));

export const objectToFormData = (obj, formData = new FormData(), parentKey = '') => {
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const newKey = parentKey ? `${parentKey}[${key}]` : key;

      if (typeof obj[key] === 'object' && obj[key] !== null) {
        // If the property is an object, recursively process it
        objectToFormData(obj[key], formData, newKey);
      } else {
        // If the property is not an object, append it to the FormData
        formData.append(newKey, obj[key]);
      }
    }
  }
  return formData;
};

export function formatDuration(seconds: string | number) {
  const duration = moment.duration(seconds, 'seconds');
  const days = Math.floor(duration.asDays());
  const hours = duration.hours();
  const minutes = duration.minutes();
  const remainingSeconds = duration.seconds();

  let result = { unit: 0, period: 'secs' }; // Initialize with default values

  if (days > 0) {
    result = { unit: days, period: days === 1 ? 'day' : 'days' };
  } else if (hours > 0) {
    result = { unit: hours, period: hours === 1 ? 'hr' : 'hrs' };
  } else if (minutes > 0) {
    result = { unit: minutes, period: minutes === 1 ? 'min' : 'mins' };
  } else if (remainingSeconds > 0) {
    result = { unit: remainingSeconds, period: remainingSeconds === 1 ? 'sec' : 'secs' };
  }

  return result;
}

export const parseIPv6ToString = (ipv6string: string): number[] => {
  const convertedIPv6String = ipv6string.toString();

  const arrayOfIpv6String: number[] = new Array(8).fill(0);

  if (convertedIPv6String === '::') return arrayOfIpv6String;

  const parse = convertedIPv6String.split(':');
  let stringLength = parse.length;
  if (stringLength > 8) stringLength = 8;
  let j = 0;

  for (let index = 0; index < stringLength; index++) {
    if (index && parse[index] === '') {
      j = 9 - stringLength + index;
      continue;
    }
    arrayOfIpv6String[j] = parseInt(`0x0${parse[index]}`);
    j++;
  }

  return arrayOfIpv6String;
};
